import Session from '../core/session'
import Config from '../core/config'

export class StorageCollector {
  private static instance: StorageCollector
  private isTracking: boolean = false
  private originalLocalStorageSetItem: typeof Storage.prototype.setItem
  private originalLocalStorageRemoveItem: typeof Storage.prototype.removeItem
  private originalLocalStorageClear: typeof Storage.prototype.clear
  private originalSessionStorageSetItem: typeof Storage.prototype.setItem
  private originalSessionStorageRemoveItem: typeof Storage.prototype.removeItem
  private originalSessionStorageClear: typeof Storage.prototype.clear

  private constructor() {
    this.originalLocalStorageSetItem = Storage.prototype.setItem
    this.originalLocalStorageRemoveItem = Storage.prototype.removeItem
    this.originalLocalStorageClear = Storage.prototype.clear
    this.originalSessionStorageSetItem = Storage.prototype.setItem
    this.originalSessionStorageRemoveItem = Storage.prototype.removeItem
    this.originalSessionStorageClear = Storage.prototype.clear
  }

  public static getInstance(): StorageCollector {
    if (!StorageCollector.instance) {
      StorageCollector.instance = new StorageCollector()
    }
    return StorageCollector.instance
  }

  /**
   * Start tracking storage operations
   */
  public start(): void {
    if (this.isTracking) {
      return
    }

    this.isTracking = true

    // Intercept localStorage
    this.interceptLocalStorage()

    // Intercept sessionStorage
    this.interceptSessionStorage()

    // Track initial storage state
    this.trackStorageSnapshot()
  }

  /**
   * Intercept localStorage operations
   */
  private interceptLocalStorage(): void {
    const self = this

    Storage.prototype.setItem = function (key: string, value: string): void {
      // Don't track our own SDK storage
      if (key.startsWith('_radar_snap_')) {
        return self.originalLocalStorageSetItem.apply(this, [key, value])
      }

      const size = new Blob([value]).size
      const category = self.categorizeStorageKey(key)

      try {
        const result = self.originalLocalStorageSetItem.apply(this, [key, value])

        self.trackStorageOperation({
          storage_type: 'localStorage',
          operation: 'set',
          key: key,
          size: size,
          category: category
        })

        return result
      } catch (error: any) {
        // Track quota exceeded error
        self.trackStorageError({
          storage_type: 'localStorage',
          operation: 'set',
          key: key,
          error_type: 'quota_exceeded',
          error_message: error.message
        })

        throw error
      }
    }

    Storage.prototype.removeItem = function (key: string): void {
      if (key.startsWith('_radar_snap_')) {
        return self.originalLocalStorageRemoveItem.apply(this, [key])
      }

      const result = self.originalLocalStorageRemoveItem.apply(this, [key])

      self.trackStorageOperation({
        storage_type: 'localStorage',
        operation: 'remove',
        key: key
      })

      return result
    }

    Storage.prototype.clear = function (): void {
      const result = self.originalLocalStorageClear.apply(this)

      self.trackStorageOperation({
        storage_type: 'localStorage',
        operation: 'clear'
      })

      return result
    }
  }

  /**
   * Intercept sessionStorage operations (similar to localStorage)
   */
  private interceptSessionStorage(): void {
    const self = this
    const sessionStorage = window.sessionStorage
    const originalSetItem = sessionStorage.setItem.bind(sessionStorage)
    const originalRemoveItem = sessionStorage.removeItem.bind(sessionStorage)
    const originalClear = sessionStorage.clear.bind(sessionStorage)

    sessionStorage.setItem = function (key: string, value: string): void {
      if (key.startsWith('_radar_snap_')) {
        return originalSetItem(key, value)
      }

      const size = new Blob([value]).size
      const category = self.categorizeStorageKey(key)

      try {
        const result = originalSetItem(key, value)

        self.trackStorageOperation({
          storage_type: 'sessionStorage',
          operation: 'set',
          key: key,
          size: size,
          category: category
        })

        return result
      } catch (error: any) {
        self.trackStorageError({
          storage_type: 'sessionStorage',
          operation: 'set',
          key: key,
          error_type: 'quota_exceeded',
          error_message: error.message
        })

        throw error
      }
    }

    sessionStorage.removeItem = function (key: string): void {
      if (key.startsWith('_radar_snap_')) {
        return originalRemoveItem(key)
      }

      const result = originalRemoveItem(key)

      self.trackStorageOperation({
        storage_type: 'sessionStorage',
        operation: 'remove',
        key: key
      })

      return result
    }

    sessionStorage.clear = function (): void {
      const result = originalClear()

      self.trackStorageOperation({
        storage_type: 'sessionStorage',
        operation: 'clear'
      })

      return result
    }
  }

  /**
   * Track storage operation
   */
  private trackStorageOperation(data: {
    storage_type: string
    operation: string
    key?: string
    size?: number
    category?: string
  }): void {
    Session.trackEvent('storage_operation', data)

    if (Config.isDebug()) {
      console.log('[RadarSnap] Storage operation:', data.operation, data.storage_type, data.key)
    }
  }

  /**
   * Track storage error
   */
  private trackStorageError(data: {
    storage_type: string
    operation: string
    key?: string
    error_type: string
    error_message: string
  }): void {
    Session.trackEvent('storage_error', data)

    if (Config.isDebug()) {
      console.error('[RadarSnap] Storage error:', data.error_type, data.storage_type)
    }
  }

  /**
   * Track initial storage snapshot
   */
  private trackStorageSnapshot(): void {
    try {
      const localStorageSize = this.calculateStorageSize(window.localStorage)
      const sessionStorageSize = this.calculateStorageSize(window.sessionStorage)
      const cookieSize = new Blob([document.cookie]).size

      Session.trackEvent('storage_snapshot', {
        localStorage_size: localStorageSize,
        sessionStorage_size: sessionStorageSize,
        cookie_size: cookieSize,
        localStorage_items: this.getStorageItemsByCategory(window.localStorage),
        sessionStorage_items: this.getStorageItemsByCategory(window.sessionStorage)
      })
    } catch (error) {
      if (Config.isDebug()) {
        console.warn('[RadarSnap] Failed to capture storage snapshot:', error)
      }
    }
  }

  /**
   * Calculate total storage size
   */
  private calculateStorageSize(storage: Storage): number {
    let totalSize = 0
    for (let i = 0; i < storage.length; i++) {
      const key = storage.key(i)
      if (key && !key.startsWith('_radar_snap_')) {
        const value = storage.getItem(key) || ''
        totalSize += new Blob([key, value]).size
      }
    }
    return totalSize
  }

  /**
   * Get storage items grouped by category
   */
  private getStorageItemsByCategory(storage: Storage): Record<string, number> {
    const categories: Record<string, number> = {}
    for (let i = 0; i < storage.length; i++) {
      const key = storage.key(i)
      if (key && !key.startsWith('_radar_snap_')) {
        const category = this.categorizeStorageKey(key)
        const value = storage.getItem(key) || ''
        const size = new Blob([value]).size

        categories[category] = (categories[category] || 0) + size
      }
    }
    return categories
  }

  /**
   * Categorize storage key based on common patterns
   */
  private categorizeStorageKey(key: string): string {
    const lowerKey = key.toLowerCase()

    if (lowerKey.includes('auth') || lowerKey.includes('token') || lowerKey.includes('session')) {
      return 'auth'
    }
    if (lowerKey.includes('user') || lowerKey.includes('profile')) {
      return 'user_preferences'
    }
    if (lowerKey.includes('cache') || lowerKey.includes('temp')) {
      return 'cache'
    }
    if (lowerKey.includes('analytics') || lowerKey.includes('tracking')) {
      return 'analytics'
    }

    return 'other'
  }

  /**
   * Stop tracking
   */
  public stop(): void {
    this.isTracking = false
    Storage.prototype.setItem = this.originalLocalStorageSetItem
    Storage.prototype.removeItem = this.originalLocalStorageRemoveItem
    Storage.prototype.clear = this.originalLocalStorageClear
  }
}

export default StorageCollector.getInstance()